// ─── ResultsCallModal ────────────────────────────────────────────────────
// 2026-07-26 — « Préparer l'appel résultats » (cadrage = réponses écrites
// d'Anissa, docs/SPEC-appel-resultats.md). Modal 100 % PRIVÉE ouverte
// depuis l'étape « Saisie résultats » : tableau par thème rendu par le
// CODE (les statuts sont ceux qu'Anissa a posés à la saisie), points
// d'appel pré-rédigés par l'IA à la demande (jamais auto-sauvés), et note
// d'appel datée après coup (remontée au briefing pré-RDV suivant).
//
// V1 Mac (réponse Q8) — pas d'optimisation téléphone.

import { useMemo, useRef, useState } from "react";
import { useModalA11y } from "../hooks/useModalA11y";
import { buildResultsCallData } from "../services/buildResultsCallData";
import { suggestResultsCall, ForbiddenPhraseError } from "../services/suggestResultsCall";
import { logResultsCallNote } from "../services/journeyState";

const STATUS_COLORS = {
  eleve: "#d4806c",
  sous_optimal: "#e8a040",
  surveiller: "#e8c26a",
  optimal: "#82c39e",
  non_interpretable: "#8a8a7a",
};

function formatNoteDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("fr-CH", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function ResultsCallModal({
  prenom,
  form,
  resultsData,
  clientId,
  initialNotes = [],
  onClose,
}) {
  const data = useMemo(
    () => buildResultsCallData({ journeyState: { results_data: resultsData }, form, prenom }),
    [resultsData, form, prenom],
  );

  const [ai, setAi] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiErr, setAiErr] = useState(null);

  const [note, setNote] = useState("");
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteErr, setNoteErr] = useState(null);
  const [notes, setNotes] = useState(
    Array.isArray(initialNotes) ? [...initialNotes].reverse() : [],
  );

  const panelRef = useRef(null);
  useModalA11y({ onClose, busy: aiBusy || noteBusy, panelRef });

  const handleGenerate = async () => {
    setAiBusy(true);
    setAiErr(null);
    try {
      const result = await suggestResultsCall(data);
      setAi(result);
    } catch (e) {
      if (e instanceof ForbiddenPhraseError) {
        // Fail-closed assumé : on n'affiche RIEN d'une sortie contaminée.
        setAiErr(`Proposition rejetée (formulation interdite : ${e.phrases.join(", ")}). Relance la pré-rédaction.`);
      } else {
        setAiErr(e?.message || "La pré-rédaction a échoué.");
      }
    } finally {
      setAiBusy(false);
    }
  };

  const handleSaveNote = async () => {
    if (!note.trim() || noteBusy) return;
    setNoteBusy(true);
    setNoteErr(null);
    try {
      const entry = await logResultsCallNote(clientId, note);
      setNotes((prev) => [entry, ...prev]);
      setNote("");
    } catch (e) {
      setNoteErr(e?.message || "Impossible d'enregistrer la note.");
    } finally {
      setNoteBusy(false);
    }
  };

  const sectionHead = (txt) => (
    <div className="jrn-brief-section__head">
      <span className="jrn-brief-section__label">{txt}</span>
      <span className="jrn-brief-section__rule" aria-hidden="true" />
    </div>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Préparer l'appel résultats — ${prenom || "cliente"}`}
      onClick={onClose}
      className="jrn-brief-overlay"
    >
      <div ref={panelRef} onClick={(e) => e.stopPropagation()} className="jrn-brief-panel">
        <div className="jrn-brief__header">
          <div>
            <p className="jrn-brief__eyebrow">Préparer l'appel résultats</p>
            <h2 style={{ margin: "2px 0 0" }}>{prenom || "Cliente"}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Fermer" className="jrn-btn jrn-btn--soft">✕</button>
        </div>

        <p style={{ fontSize: ".72rem", color: "rgba(255,255,255,.45)", margin: "6px 0 14px", lineHeight: 1.5 }}>
          Privé — rien de cette page ne part vers la cliente. Les statuts sont ceux que tu as posés à la saisie.
          {data.sensitive && (
            <strong style={{ display: "block", color: "#e8c26a", marginTop: 4 }}>
              Profil sensible : les points d'échange proposés sont sans chiffres, neutres, sans jugement poids/aliments.
            </strong>
          )}
        </p>

        {/* ── Lecture privée par thème (rendue par le code, sans IA) ── */}
        {sectionHead("Ta lecture, par thème")}
        {data.themes.map((theme) => (
          <div key={theme.axis} style={{ marginBottom: 14 }}>
            <p style={{ margin: "0 0 6px", fontSize: ".8rem", fontWeight: 600, color: "#cfe0d2" }}>{theme.label}</p>
            {[...theme.attention, ...theme.rassurer, ...theme.autres].map((item, i) => (
              <div key={`${item.label}-${i}`} style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "baseline", padding: "5px 0", borderBottom: "1px solid rgba(255,255,255,.05)", fontSize: ".78rem" }}>
                <span style={{ color: "#f0efe6", fontWeight: 500 }}>{item.label}</span>
                {item.value && (
                  <span style={{ color: "rgba(255,255,255,.7)" }}>
                    {item.value}{item.unit ? ` ${item.unit}` : ""}
                  </span>
                )}
                {item.status_label && (
                  <span style={{ color: STATUS_COLORS[item.status] || "#8a8a7a", fontWeight: 600 }}>{item.status_label}</span>
                )}
                {/* Revue clinique-guard R1 (2026-07-26) : les ref_range du
                    catalogue markers.js n'ont JAMAIS été validées par Anissa
                    (le fichier l'exige explicitement) — on ne les affiche PAS
                    tant qu'elle ne les a pas relues. Les données les portent
                    déjà (item.ref_range) : réactiver cet affichage après sa
                    validation du catalogue. */}
                {item.synthesis && (
                  <span style={{ flexBasis: "100%", color: "rgba(255,255,255,.55)", fontSize: ".72rem" }}>{item.synthesis}</span>
                )}
              </div>
            ))}
          </div>
        ))}
        {data.global_synthesis && (
          <p style={{ fontSize: ".76rem", color: "rgba(255,255,255,.6)", fontStyle: "italic", margin: "0 0 16px" }}>
            Ta synthèse globale : {data.global_synthesis}
          </p>
        )}

        {/* ── Points d'appel IA (brouillon, à la demande, jamais sauvé) ── */}
        {sectionHead("Points d'appel")}
        {!ai && (
          <button type="button" className="jrn-btn" disabled={aiBusy} onClick={handleGenerate}>
            {aiBusy ? "Pré-rédaction…" : "Pré-rédiger les points d’appel"}
          </button>
        )}
        {aiErr && <p style={{ color: "#d4806c", fontSize: ".76rem", marginTop: 8 }}>{aiErr}</p>}

        {ai && (
          <div style={{ fontSize: ".8rem", lineHeight: 1.55, color: "rgba(255,255,255,.82)" }}>
            <p style={{ margin: "0 0 4px", fontWeight: 600, color: "#82c39e" }}>1 · Ouvrir — rassurer et donner la vision globale</p>
            <p style={{ margin: "0 0 8px" }}>{ai.vision_globale}</p>
            {Array.isArray(ai.a_rassurer) && ai.a_rassurer.length > 0 && (
              <ul style={{ margin: "0 0 12px", paddingLeft: 18 }}>
                {ai.a_rassurer.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            )}

            <p style={{ margin: "0 0 4px", fontWeight: 600, color: "#e8c26a" }}>2 · Expliquer — ce qui ressort, sans dramatiser</p>
            {(ai.par_theme || []).map((t, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <p style={{ margin: "0 0 2px", fontWeight: 600 }}>{t.theme}</p>
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {(t.points || []).map((p, j) => <li key={j}>{p}</li>)}
                </ul>
              </div>
            ))}

            {Array.isArray(ai.pistes_plan) && ai.pistes_plan.length > 0 && (
              <>
                <p style={{ margin: "12px 0 4px", fontWeight: 600, color: "#cfe0d2" }}>3 · La suite — pistes pour le plan (la décision t'appartient)</p>
                <ul style={{ margin: "0 0 12px", paddingLeft: 18 }}>
                  {ai.pistes_plan.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </>
            )}

            {Array.isArray(ai.questions_a_poser) && ai.questions_a_poser.length > 0 && (
              <>
                <p style={{ margin: "0 0 4px", fontWeight: 600, color: "#cfe0d2" }}>Questions à lui poser</p>
                <ul style={{ margin: "0 0 12px", paddingLeft: 18 }}>
                  {ai.questions_a_poser.map((q, i) => <li key={i}>{q}</li>)}
                </ul>
              </>
            )}

            {Array.isArray(ai.sujets_delicats) && ai.sujets_delicats.length > 0 && (
              <>
                <p style={{ margin: "0 0 4px", fontWeight: 600, color: "#d4806c" }}>Sujets délicats — façons de le dire</p>
                {ai.sujets_delicats.map((s, i) => (
                  <div key={i} style={{ marginBottom: 8 }}>
                    <p style={{ margin: "0 0 2px", fontWeight: 600 }}>{s.sujet}</p>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {(s.formulations || []).map((f, j) => <li key={j} style={{ fontStyle: "italic" }}>{f}</li>)}
                    </ul>
                  </div>
                ))}
              </>
            )}

            <button type="button" className="jrn-btn jrn-btn--soft" disabled={aiBusy} onClick={handleGenerate} style={{ marginTop: 6 }}>
              {aiBusy ? "Pré-rédaction…" : "Reproposer"}
            </button>
          </div>
        )}

        {/* ── Note d'appel (après l'appel — seule chose persistée) ── */}
        <div style={{ marginTop: 18 }}>
          {sectionHead("Après l'appel — ta note")}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Ce que tu lui as expliqué, les décisions prises, les prochaines actions…"
            rows={3}
            style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 8, color: "#f0efe6", padding: 10, fontSize: ".8rem", resize: "vertical" }}
          />
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
            <button type="button" className="jrn-btn" disabled={!note.trim() || noteBusy} onClick={handleSaveNote}>
              {noteBusy ? "Enregistrement…" : "Enregistrer la note d'appel"}
            </button>
            {noteErr && <span style={{ color: "#d4806c", fontSize: ".74rem" }}>{noteErr}</span>}
          </div>
          {notes.length > 0 && (
            <div style={{ marginTop: 10 }}>
              {notes.map((n, i) => (
                <div key={`${n.date}-${i}`} style={{ padding: "6px 0", borderTop: "1px solid rgba(255,255,255,.06)", fontSize: ".76rem" }}>
                  <span style={{ color: "rgba(255,255,255,.45)" }}>{formatNoteDate(n.date)}</span>
                  <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,.75)", whiteSpace: "pre-wrap" }}>{n.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
